import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import DemoModal from './DemoModal';
import VolumeControl from './VolumeControl';
import VideoPlayPauseButton from './VideoPlayPauseButton';
import useScrollAutoplay from '../hooks/useScrollAutoplay';

/**
 * StandardPageLayout - 4-Section Layout Template for All Interior Pages
 * 
 * All sections use min-h-[85vh] to match landing page hero dimensions
 * 
 * Structure:
 * 1. Hero Section (min-h-[85vh], gradient background)
 * 2. Content Section 1 (min-h-[85vh], white background)
 * 3. Content Section 2 (min-h-[85vh], light sage background)
 * 4. CTA Section (min-h-[85vh], gradient background)
 */

const StandardPageLayout = ({ 
  // Section 1: Hero
  heroTitle,
  heroDescription,
  heroContent = null,
  heroVideoUrl = null,
  // Overlay strength for heroVideoUrl — same gradient formula/opacity as
  // HeroMedia (landing page hero) so a shared video looks identical on
  // both pages. Defaults to the landing page's current value (0.4).
  heroVideoOverlay = 0.4,
  // Optional hero theming overrides. When provided, these override the
  // default cream gradient + black text so a page can use, e.g., a
  // midnight-navy background with light text (ProductC / AI App Builder).
  heroBackgroundClass = null,   // e.g. "bg-[#141e30]"
  heroTextLight = false,        // true → off-white headline + body text
  // Optional node rendered inside the hero section as an absolutely-
  // positioned background layer behind the centered headline/description.
  // Used by Careers to host the ambient starfield canvas. The node is
  // responsible for its own positioning (typically `absolute inset-0`).
  heroBackgroundNode = null,
  // When provided, this node fully REPLACES the standard hero <section>.
  // Used by pages that need a bespoke full-bleed hero (e.g. AI App Builder
  // canvas network). All other hero props above are ignored when set.
  customHero = null,
  
  // Section 2: Content Section 1
  section2HeaderContent = null,
  section2Title,
  section2Description,
  section2Content,
  
  // Section 2b: Optional intermediate Content Section (rendered between
  // sections 2 and 3). Same design language as the other content sections.
  section2bTitle,
  section2bDescription,
  section2bContent = null,
  // Optional Tailwind classes applied to section 2b's outer <section>.
  // Used by the About page to tighten the vertical gap between section 2
  // (Our Story text) and section 2b (stat boxes) via a negative top
  // margin — without altering any visible content.
  section2bSectionClassName = '',
  
  // Section 2c: Optional intermediate Content Section (rendered between
  // section 2b and section 3). Same design pattern as the other content
  // sections (motion fade-in, max-w-7xl container, identical heading/body
  // typography, bg-white).
  section2cTitle,
  section2cDescription,
  section2cContent = null,

  // Section 3: Content Section 2
  section3Title,
  section3Description,
  section3Content,
  // Optional background override for section 3's outer <section>. Defaults
  // to the standard bg-[#F5F7F5] used by every other page. Used by the
  // About page to keep proper white/grey alternation now that a new
  // section (2c) sits directly above it.
  section3BackgroundClass = 'bg-[#F5F7F5]',
  
  // Section 4: CTA
  ctaTitle,
  ctaDescription,
  ctaButtonText = "Get started",
  // Optional: if provided, the CTA button becomes a link to this href
  // instead of opening the Demo modal. Used by the Careers page to send
  // applicants to /careers/applynow. Defaults preserve every other page's
  // existing behavior unchanged.
  ctaButtonHref = null,
  ctaButtonNewTab = false,
  showCta = true
}) => {
  const [demoOpen, setDemoOpen] = useState(false);

  // Hero video controls — identical state shape & behavior to HeroMedia /
  // TripleVideoHero / MediaUploadHero. Hooks declared unconditionally even
  // when there is no video, per React rules of hooks.
  // NOTE: isMuted defaults to true so the hero video autoplays silently on
  // load (required to satisfy modern browser autoplay policies). Users can
  // click the volume button to enable audio anytime.
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);

  // Only play the video while it is scrolled into view — pauses it off-screen.
  useScrollAutoplay(videoRef, isPlaying, 0.25, !!heroVideoUrl);

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => {});
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className="w-full">
      {customHero ? (
        customHero
      ) : (
        <>
          {/* Section 1: Hero - min-h-[85vh] */}
          <section
            className={`group/video relative min-h-[85vh] flex items-center justify-center overflow-hidden ${
              heroBackgroundClass || 'bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7]'
            }`}
          >
        {/* Optional absolutely-positioned background node (e.g. ambient canvas
            animation). Rendered behind the centered headline/description but
            above the section's flat background. */}
        {heroBackgroundNode}
        {/* Optional background video — full-bleed, autoplays silently on loop.
            Sits behind a subtle cream overlay that matches the original hero
            tone so the existing black headline + paragraph stay legible
            without any text-style changes. */}
        {heroVideoUrl && (
          <>
            <video
              ref={videoRef}
              loop
              muted={isMuted}
              playsInline
              preload="metadata"
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="hero-uploaded-video"
            >
              <source src={heroVideoUrl} type="video/mp4" />
            </video>

            {/* Gradient overlay — identical formula to HeroMedia (landing
                page hero) so a shared video renders with the same
                contrast/richness on both pages. */}
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(135deg, rgba(232, 160, 184, ${heroVideoOverlay * 0.3}), rgba(244, 194, 212, ${heroVideoOverlay * 0.2})), linear-gradient(to bottom, rgba(0, 0, 0, ${heroVideoOverlay}), rgba(0, 0, 0, ${heroVideoOverlay * 0.7}))`,
              }}
            />

            {/* Centered play/pause overlay (hover-only). Volume/mute stays bottom-right. */}
            <VideoPlayPauseButton
              isPlaying={isPlaying}
              onClick={togglePlayPause}
              testId="hero-play-button"
            />

            {/* Video Control Buttons - Bottom Right (mute only, identical position) */}
            <div className="absolute bottom-6 right-6 z-20 flex items-center gap-3">
              {/* Mute/Unmute Toggle Button with Hover Volume Slider */}
              <VolumeControl
                videoRef={videoRef}
                isMuted={isMuted}
                onToggleMute={toggleMute}
                testId="hero-volume-button"
              />
            </div>
          </>
        )}

        <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center py-16 md:py-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {heroTitle && (
              <h1
                className={`text-4xl sm:text-5xl md:text-6xl font-medium mb-4 sm:mb-6 ${
                  heroTextLight ? 'text-white/95' : 'text-black/95'
                }`}
              >
                {heroTitle}
              </h1>
            )}
            {heroDescription && (
              <p
                className={`text-base sm:text-lg md:text-xl max-w-3xl mx-auto ${
                  heroTextLight ? 'text-white/70' : 'text-black/70'
                }`}
              >
                {heroDescription}
              </p>
            )}
            {heroContent}
          </motion.div>
        </div>
      </section>
        </>
      )}

      {/* Section 2: Content Section 1 - min-h-[85vh] */}
      <section className="min-h-[85vh] bg-white flex items-center justify-center">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Optional content block rendered above the section 2 title.
                Used by the About page to host the relocated hero copy. */}
            {section2HeaderContent && (
              <div
                className={`text-center max-w-4xl mx-auto ${
                  (section2Title || section2Content) ? 'mb-10 md:mb-14' : ''
                }`}
              >
                {section2HeaderContent}
              </div>
            )}
            {section2Title && (
              <div className="text-center mb-10 md:mb-16">
                <h2 className="text-3xl md:text-4xl font-medium text-black/90 mb-4 sm:mb-6">
                  {section2Title}
                </h2>
                {section2Description && (
                  <p className="text-base sm:text-lg text-black/70 max-w-3xl mx-auto">
                    {section2Description}
                  </p>
                )}
              </div>
            )}
            {section2Content}
          </motion.div>
        </div>
      </section>

      {/* Section 2b: Optional intermediate Content Section - min-h-[85vh].
          Same design pattern as sections 2 & 3 (motion fade-in, max-w-7xl
          container, identical heading/body typography). Background uses
          bg-white to keep the page's color rhythm unchanged below it. */}
      {section2bContent && (
        <section className={`min-h-[85vh] bg-white flex items-center justify-center ${section2bSectionClassName}`}>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full py-16 md:py-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {section2bTitle && (
                <div className="text-center mb-10 md:mb-16">
                  <h2 className="text-3xl md:text-4xl font-medium text-black/90 mb-4 sm:mb-6">
                    {section2bTitle}
                  </h2>
                  {section2bDescription && (
                    <p className="text-base sm:text-lg text-black/70 max-w-3xl mx-auto">
                      {section2bDescription}
                    </p>
                  )}
                </div>
              )}
              {section2bContent}
            </motion.div>
          </div>
        </section>
      )}

      {/* Section 2c: Optional intermediate Content Section - min-h-[85vh].
          Same design pattern as sections 2, 2b & 3 (motion fade-in,
          max-w-7xl container, identical heading/body typography). */}
      {section2cContent && (
        <section className="min-h-[85vh] bg-[#F5F7F5] flex items-center justify-center">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full py-16 md:py-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {section2cTitle && (
                <div className="text-center mb-10 md:mb-16">
                  <h2 className="text-3xl md:text-4xl font-medium text-black/90 mb-4 sm:mb-6">
                    {section2cTitle}
                  </h2>
                  {section2cDescription && (
                    <p className="text-base sm:text-lg text-black/70 max-w-3xl mx-auto">
                      {section2cDescription}
                    </p>
                  )}
                </div>
              )}
              {section2cContent}
            </motion.div>
          </div>
        </section>
      )}

      {/* Section 3: Content Section 2 - min-h-[85vh] */}
      <section className={`min-h-[85vh] flex items-center justify-center ${section3BackgroundClass}`}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {section3Title && (
              <div className="text-center mb-10 md:mb-16">
                <h2 className="text-3xl md:text-4xl font-medium text-black/90 mb-4 sm:mb-6">
                  {section3Title}
                </h2>
                {section3Description && (
                  <p className="text-base sm:text-lg text-black/70 max-w-3xl mx-auto">
                    {section3Description}
                  </p>
                )}
              </div>
            )}
            {section3Content}
          </motion.div>
        </div>
      </section>

      {/* Section 4: CTA Section */}
      {showCta && (
        <section className="py-14 md:py-20 bg-gradient-to-br from-[#E8E4DC] to-[#D4DDD1]">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-medium text-black/95 mb-4 sm:mb-6">
                {ctaTitle}
              </h2>
              <p className="text-base sm:text-lg text-black/70 mb-6 sm:mb-8 max-w-2xl mx-auto">
                {ctaDescription}
              </p>
              <Button
                size="lg"
                {...(ctaButtonHref
                  ? {
                      asChild: true,
                    }
                  : {
                      onClick: () => setDemoOpen(true),
                    })}
                className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-105 transition-all text-base px-8"
              >
                {ctaButtonHref ? (
                  <a
                    href={ctaButtonHref}
                    {...(ctaButtonNewTab
                      ? { target: '_blank', rel: 'noopener noreferrer' }
                      : {})}
                    data-testid="cta-button-link"
                  >
                    {ctaButtonText}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                ) : (
                  <>
                    {ctaButtonText}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </motion.div>
          </div>
        </section>
      )}

      <DemoModal open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
};

export default StandardPageLayout;
